import { z } from 'zod';
import type { ConfigTahiti } from '../config';
import { ServiceIndisponibleError } from '../erreurs';
import { CODE_LANGUE, type Filtre, idSchema, lireLignes } from './h2data';
import type { AppelHttp } from './http';
import { TABLE_TACHE } from './taches';

/**
 * The identifiers of Tahiti that Rosetta v5 needs and that differ from one environment to
 * the next, read BY CODE once per session (never written in the code or the configuration,
 * as the reference script of the team does): the French language, the methodology
 * `rose_methodology_v5`, the business process `rosetta_v5_main` and the type of the task
 * `rose_task_v5_multi_translation`.
 *
 * The project, the activity, the workspace and the category stay in the configuration.
 */
export const CODE_METHODOLOGIE = 'rose_methodology_v5';
export const CODE_PROCESSUS = 'rosetta_v5_main';

export interface ContexteRosetta {
  /** `rose_language` French: the source AND the target language (Rosetta rewrites French in French). */
  langueId: number;
  /** `rose_methodology` v5. */
  methodologieId: number;
  /** `h2_bp` `rosetta_v5_main`. */
  processusId: number;
  /** `h2_tasktype` whose `physical_name` is `rose_task_v5_multi_translation`. */
  typeTacheId: number;
}

const ligneSchema = z.object({ id: idSchema });

/** The one row of `table` that matches `where`, or an error: a code that is not unique is a gap. */
async function exactementUne(
  appel: AppelHttp,
  config: ConfigTahiti,
  jeton: string,
  table: string,
  champs: readonly string[],
  where: Filtre,
): Promise<number> {
  const lignes = await lireLignes(
    appel,
    config,
    jeton,
    { table, fields: ['id', ...champs], where, limit: 5 },
    ligneSchema,
  );
  const [ligne] = lignes;
  if (lignes.length !== 1 || ligne === undefined) {
    throw new ServiceIndisponibleError(
      `Tahiti: 1 record expected in ${table} for ${where[0]} = ${where[1]}, found ${lignes.length}`,
    );
  }
  return Number(ligne.id);
}

/**
 * Resolves the identifiers above, in parallel. A failed resolution is never kept by the
 * caller: the next translation tries again.
 *
 * TO VALIDATE(#6): the codes and the physical name are those of the reference script, on
 * the dev environment; the other environments are not known.
 */
export async function resoudreContexte(
  appel: AppelHttp,
  config: ConfigTahiti,
  jeton: string,
): Promise<ContexteRosetta> {
  const [langueId, methodologieId, processusId, typeTacheId] = await Promise.all([
    exactementUne(
      appel,
      config,
      jeton,
      'rose_language',
      ['code', 'title'],
      ['code', CODE_LANGUE, '='],
    ),
    exactementUne(
      appel,
      config,
      jeton,
      'rose_methodology',
      ['code', 'title'],
      ['code', CODE_METHODOLOGIE, '='],
    ),
    exactementUne(appel, config, jeton, 'h2_bp', ['code', 'title'], ['code', CODE_PROCESSUS, '=']),
    exactementUne(
      appel,
      config,
      jeton,
      'h2_tasktype',
      ['physical_name', 'title'],
      ['physical_name', TABLE_TACHE, '='],
    ),
  ]);
  return { langueId, methodologieId, processusId, typeTacheId };
}
